import { useEffect, useState } from 'react';
import { CheckCircle, Clock, Mail, Sparkles, ArrowRight } from 'lucide-react';

export function OnboardingCompletePage() {
  const [showConfetti, setShowConfetti] = useState(true);

  useEffect(() => {
    // Hide confetti animation after 3 seconds
    const timer = setTimeout(() => setShowConfetti(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50">
      {/* Confetti effect */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                backgroundColor: ['#3AABE8', '#47B7E7', '#75C9ED', '#F9B03F', '#FACF75'][Math.floor(Math.random() * 5)],
                width: '10px',
                height: '10px',
                borderRadius: Math.random() > 0.5 ? '50%' : '0',
              }}
            />
          ))}
        </div>
      )}

      <div className="max-w-2xl mx-auto px-4 py-16 sm:py-24">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            You're All Set!
          </h1>
          <p className="text-xl text-gray-600">
            Thank you for completing your onboarding. We're excited to build your website!
          </p>
        </div>

        {/* What's Next Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary-500" />
            What Happens Next
          </h2>

          <div className="space-y-6">
            {/* Step 1 */}
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                <span className="text-primary-600 font-semibold">1</span>
              </div>
              <div>
                <h3 className="font-medium text-gray-900">AI Content Generation</h3>
                <p className="text-gray-600 text-sm mt-1">
                  Our AI is now creating custom content tailored to your business, including compelling copy and layout.
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                <span className="text-primary-600 font-semibold">2</span>
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Quality Assurance</h3>
                <p className="text-gray-600 text-sm mt-1">
                  We run automated checks to ensure your site loads fast, looks great on all devices, and meets accessibility standards.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                <span className="text-primary-600 font-semibold">3</span>
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Preview Link</h3>
                <p className="text-gray-600 text-sm mt-1">
                  You'll receive an email with a link to preview your site. Review it and let us know if you'd like any changes.
                </p>
              </div>
            </div>

            {/* Step 4 */}
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                <span className="text-primary-600 font-semibold">4</span>
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Go Live!</h3>
                <p className="text-gray-600 text-sm mt-1">
                  Once you approve, we'll connect your domain and your professional website will be live for the world to see.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Timeline Card */}
        <div className="bg-primary-500 rounded-2xl p-6 mb-8 text-white">
          <div className="flex items-center gap-3 mb-2">
            <Clock className="w-6 h-6" />
            <h3 className="font-semibold text-lg">Expected Timeline</h3>
          </div>
          <p className="text-primary-100">
            Your preview will be ready within <strong className="text-white">24-48 hours</strong>.
            We'll send you an email as soon as it's ready for review.
          </p>
        </div>

        {/* Email Reminder */}
        <div className="bg-accent-50 border border-accent-200 rounded-xl p-5 mb-8">
          <div className="flex gap-3">
            <Mail className="w-5 h-5 text-accent-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-accent-800">Check Your Inbox</h3>
              <p className="text-accent-700 text-sm mt-1">
                We've sent you a confirmation email with these details. If you don't see it, check your spam folder.
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <p className="text-gray-500 mb-4">Have questions? We're here to help.</p>
          <a
            href="mailto:support@niftee.ai"
            className="inline-flex items-center gap-2 text-primary-500 hover:text-primary-600 font-medium"
          >
            Contact Support
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-200 text-center">
          <p className="text-sm text-gray-500">
            &copy; {new Date().getFullYear()} niftee Digital. All rights reserved.
          </p>
        </div>
      </div>

      <style>{`
        @keyframes confetti {
          0% {
            transform: translateY(-100vh) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        .animate-confetti {
          animation: confetti 3s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
